// =============================================================================
// features/configuracoes/comercial/ReasonListEditor.tsx — Editor de lista de
// motivos (cancelamento/supressão, doc 26 §6.9). Reutilizado por
// CancelamentosSection.tsx para os dois grupos.
//
// Limites espelham `packages/shared-schemas/src/sales-settings.ts`
// (cancellationReasons/suppressionReasons): item 1–200 chars, lista até 50.
// =============================================================================

import * as React from 'react';

import { Button } from '../../../components/ui/Button';
import { Input } from '../../../components/ui/Input';

const MAX_ITEMS = 50;
const MAX_ITEM_LENGTH = 200;

interface ReasonListEditorProps {
  idPrefix: string;
  label: string;
  hint: string;
  items: string[];
  onChange: (items: string[]) => void;
  disabled?: boolean;
}

export function ReasonListEditor({
  idPrefix,
  label,
  hint,
  items,
  onChange,
  disabled = false,
}: ReasonListEditorProps): React.JSX.Element {
  const [draft, setDraft] = React.useState('');
  const [error, setError] = React.useState<string | undefined>(undefined);

  const addItem = (): void => {
    const value = draft.trim();
    if (!value) return;
    if (value.length > MAX_ITEM_LENGTH) {
      setError(`Motivo muito longo — máximo ${MAX_ITEM_LENGTH} caracteres`);
      return;
    }
    if (items.includes(value)) {
      setError('Esse motivo já está na lista');
      return;
    }
    if (items.length >= MAX_ITEMS) {
      setError(`Máximo de ${MAX_ITEMS} motivos`);
      return;
    }
    setError(undefined);
    onChange([...items, value]);
    setDraft('');
  };

  const removeItem = (value: string): void => {
    onChange(items.filter((item) => item !== value));
  };

  return (
    <div className="flex flex-col gap-2">
      <Input
        id={`${idPrefix}-new`}
        label={label}
        hint={error ? undefined : hint}
        error={error}
        placeholder="Ex: Cliente desistiu"
        value={draft}
        disabled={disabled}
        onChange={(e) => {
          setDraft(e.target.value);
          if (error) setError(undefined);
        }}
        onKeyDown={(e) => {
          if (e.key === 'Enter') {
            e.preventDefault();
            addItem();
          }
        }}
      />
      <div className="flex justify-end">
        <Button type="button" variant="outline" size="sm" disabled={disabled} onClick={addItem}>
          Adicionar motivo
        </Button>
      </div>

      {items.length > 0 && (
        <ul className="flex flex-wrap gap-2 mt-1" aria-label={`${label} cadastrados`}>
          {items.map((item) => (
            <li
              key={item}
              className="flex items-center gap-2 rounded-pill px-3 py-1.5"
              style={{ background: 'var(--surface-muted)', boxShadow: 'var(--elev-1)' }}
            >
              <span className="font-sans text-ink" style={{ fontSize: 'var(--text-xs)' }}>
                {item}
              </span>
              <button
                type="button"
                disabled={disabled}
                onClick={() => removeItem(item)}
                aria-label={`Remover motivo ${item}`}
                className="text-ink-3 hover:text-danger transition-colors duration-fast focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-azul/15 rounded-xs disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <svg
                  viewBox="0 0 16 16"
                  fill="currentColor"
                  className="w-3.5 h-3.5"
                  aria-hidden="true"
                >
                  <path d="M4.293 4.293a1 1 0 011.414 0L8 6.586l2.293-2.293a1 1 0 111.414 1.414L9.414 8l2.293 2.293a1 1 0 01-1.414 1.414L8 9.414l-2.293 2.293a1 1 0 01-1.414-1.414L6.586 8 4.293 5.707a1 1 0 010-1.414z" />
                </svg>
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
